<script lang="ts" setup>

import type {Media} from "~/types/Media";

const {photos = null} = defineProps<{
  photos: Media[] | null;
}>();

// Lightbox state
const lightboxOpen = ref(false);
const selectedPhotoIndex = ref(0);

const openLightbox = (index: number) => {
  selectedPhotoIndex.value = index;
  lightboxOpen.value = true;
};

const closeLightbox = () => {
  lightboxOpen.value = false;
};

const nextPhoto = () => {
  if (photos && photos?.length > 0) {
    selectedPhotoIndex.value = (selectedPhotoIndex.value + 1) % photos?.length;
  }
};

const prevPhoto = () => {
  if (photos && photos?.length > 0) {
    selectedPhotoIndex.value = (selectedPhotoIndex.value - 1 + photos?.length) % photos?.length;
  }
};

const selectPhoto = (index: number) => {
  selectedPhotoIndex.value = index;
};


</script>

<template>
  <!-- Images -->
  <div v-if="photos && photos?.length > 0" class="photo-gallery">
    <div
        v-for="(photo, index) in photos"
        :key="photo.id"
        class="photo-item"
    >
      <v-img
          :src="photo.thumb_url"
          aspect-ratio="1"
          cover
          class="rounded-lg photo-thumbnail"
          @click="openLightbox(index)"
      />
    </div>
  </div>

  <!-- Lightbox Overlay -->
  <Teleport to="body">
    <div
        v-if="photos && lightboxOpen"
        class="lightbox-overlay"
        @click.self="closeLightbox"
    >
      <!-- Main Image -->
      <div class="lightbox-content" @click.stop>
        <v-btn
            icon
            size="small"
            class="lightbox-close-btn"
            @click.stop="closeLightbox"
        >
          <v-icon size="24">mdi-close</v-icon>
        </v-btn>
        <div class="image-wrapper">
          <v-img
              :src="photos[selectedPhotoIndex]?.url"
              cover
              class="main-image"
          />
          <v-btn
              icon
              class="nav-btn nav-btn-left"
              size="small"
              @click.stop="prevPhoto"
          >
            <v-icon size="32">mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn
              icon
              class="nav-btn nav-btn-right"
              size="small"
              @click.stop="nextPhoto"
          >
            <v-icon size="32">mdi-chevron-right</v-icon>
          </v-btn>
        </div>

        <!-- Thumbnail Strip -->
        <div class="thumbnail-strip">
          <div class="thumbnail-container">
            <div
                v-for="(photo, index) in photos"
                :key="photo.id"
                :class="['thumbnail', { 'thumbnail-active': index === selectedPhotoIndex }]"
                @click.stop="selectPhoto(index)"
            >
              <v-img
                  :src="photo.thumb_url"
                  cover
                  class="thumbnail-image"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
// Photo gallery layout
.photo-gallery {
  display: grid;
  grid-template-columns: repeat(5, 80px);
  gap: 20px;
}

// Photo thumbnail cursor
.photo-thumbnail {
  cursor: pointer;
  width: 80px !important;
  height: 80px !important;
  border-radius: 16px !important;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
}

// Mobile: horizontal scroll with invisible scrollbar
@media (max-width: 768px) {
  .photo-gallery {
    display: flex;
    overflow-x: auto;
    overflow-y: hidden;
    gap: 8px;
    scrollbar-width: none; // Firefox
    -ms-overflow-style: none; // IE/Edge

    &::-webkit-scrollbar {
      display: none; // Chrome/Safari/Opera
    }
  }

  .photo-item {
    flex-shrink: 0;
  }
}

// Lightbox overlay
.lightbox-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.9) !important;
  z-index: 10000;
}

.lightbox-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: min(90vw, 800px);
  max-height: 90vh;
  gap: 16px;
  position: relative;
  padding: 24px;
}

.image-wrapper {
  position: relative;
  width: min(90vw, 800px);
  height: min(70vh, 700px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.main-image {
  width: min(90vw, 800px);
  height: min(70vh, 700px);
  object-fit: contain;
  border-radius: 16px;
}

.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(0, 0, 0, 0.5) !important;
  backdrop-filter: blur(8px);

  :deep(.v-icon) {
    color: white !important;
  }
}

.lightbox-close-btn {
  position: fixed;
  top: 16px;
  right: 16px;
  background-color: rgba(0, 0, 0, 0.6) !important;
  color: white !important;
  z-index: 10001;
}

.nav-btn-left {
  left: 12px;
}

.nav-btn-right {
  right: 12px;
}

.thumbnail-strip {
  width: 100%;
  max-height: 110px;
  padding: 0 16px;
  overflow-x: auto;
  overflow-y: hidden;
}

.thumbnail-container {
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: center;
  align-items: center;
}

.thumbnail {
  width: 80px;
  height: 80px;
  flex-shrink: 0;
  border-radius: 12px;
  cursor: pointer;
  opacity: 0.6;
  transition: all 0.2s;
  border: 2px solid transparent;
  overflow: hidden;
}

.thumbnail-active {
  opacity: 1;
  border-color: white;
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

@media (max-width: 600px) {
  .lightbox-content {
    width: 100vw;
    max-height: 100vh;
    border-radius: 0;
    padding: 16px;
  }

  .image-wrapper {
    width: 100vw;
    height: 100vw;
  }

  .main-image {
    width: 100vw;
    height: 100vw;
    border-radius: 16px;
  }

  .thumbnail-strip {
    padding: 0 8px;
  }

  .thumbnail {
    width: 64px;
    height: 64px;
  }
}
</style>